import React from "react";
import Button from "@adsk/alloy-react-button";
import Modal from "@adsk/alloy-react-modal";
import { IssueFailureInfo, Issue } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { getIssuesByIds } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";

const is4xxErrorCode = (statusCode: number) => {
  return statusCode >= 400 && statusCode < 500;
};

const isServerErrorCode = (statusCode: number) => {
  // no such thing as status codes above 600 and if for some reasons a server sends one
  // it's ok if it counts as a server error
  // so checking for any code equal to or greater than 500 is reasonable
  return statusCode >= 500;
};

export const IssueBulkActionPartialFailureModal = ({
  failureInfo,
  onAcknowledgement,
  actionType,
}: {
  failureInfo: IssueFailureInfo[] | null;
  onAcknowledgement: () => void;
  actionType: "edit" | "delete";
}) => {
  const { T } = useTranslations();
  const translationPrefix = `issueBulkEdit.${actionType}PartialFailure`;
  const selectIssuesByIds = useSelector(getIssuesByIds);
  const permissionsErrorIssues = failureInfo
    ? selectIssuesByIds(
        failureInfo
          .filter(issueFailure => is4xxErrorCode(issueFailure.statusCode))
          .map(issueFailure => issueFailure.issueUid)
      )
    : [];

  const serverErrorIssues =
    failureInfo?.filter(issueFailure =>
      isServerErrorCode(issueFailure.statusCode)
    ) ?? [];

  return (
    <Modal open={!!failureInfo} size={Modal.SIZES.MEDIUM} height="inherit">
      <Modal.Header>
        {T.translate(`${translationPrefix}.title`, {
          failureCount: failureInfo?.length,
        })}
        <Modal.Close onClick={onAcknowledgement} />
      </Modal.Header>
      <Modal.Body>
        {
          // if there are any server errors, we lump it all together
          serverErrorIssues.length ? (
            <>
              <p>
                {T.translate(`${translationPrefix}.serverErrorDescription`, {
                  failureCount: failureInfo?.length,
                })}
              </p>
            </>
          ) : (
            <>
              <p>
                {T.translate(`${translationPrefix}.description`, {
                  failureCount: failureInfo?.length,
                })}
              </p>
              <p>{T.translate(`${translationPrefix}.permissionsSuggestion`)}</p>
              <p>
                {permissionsErrorIssues.map((issue: Issue) => {
                  return (
                    <div key={issue.id}>
                      - #{issue.displayId} - {issue.title}
                    </div>
                  );
                })}
              </p>
            </>
          )
        }
      </Modal.Body>

      <Modal.Footer disableBorder>
        <Button variant={Button.VARIANTS.PRIMARY} onClick={onAcknowledgement}>
          {T.translate("actions.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
